// ShareActionModal.js - Module de partage d'actions avec contexte enrichi
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Share2, Send, Users, Calendar, Mail, MessageCircle, 
  X, Plus, Eye, EyeOff, Sparkles, FileText, Target,
  Clock, User, CheckCircle, AlertTriangle, Info,
  Lightbulb, HelpCircle, Brain, Copy, ExternalLink
} from 'lucide-react';
import { askGPT } from '../components/OpenAIService';
import { sendEmail } from '../components/EmailService';

// =============================================
// CONFIGURATION DES TYPES DE PARTAGE
// =============================================

const SHARE_TYPES = {
  advice: {
    id: 'advice',
    title: 'Demander un avis',
    description: 'Obtenir des conseils et retours d\'expérience',
    icon: Lightbulb,
    color: 'blue',
    defaultMessage: 'Je travaille actuellement sur cette action et j\'aimerais avoir ton avis...',
    suggestedActions: ['meeting', 'email_response']
  },
  assistance: {
    id: 'assistance', 
    title: 'Demander de l\'aide',
    description: 'Obtenir une assistance technique ou méthodologique',
    icon: HelpCircle,
    color: 'orange',
    defaultMessage: 'J\'aurais besoin de ton aide pour avancer sur cette action...',
    suggestedActions: ['meeting', 'platform_response', 'email_response']
  },
  collaboration: {
    id: 'collaboration',
    title: 'Proposer une collaboration',
    description: 'Inviter à travailler ensemble sur une étape du projet',
    icon: Users,
    color: 'purple',
    defaultMessage: 'Cette action pourrait bénéficier de notre collaboration...',
    suggestedActions: ['meeting', 'platform_response']
  },
  intervention: {
    id: 'intervention',
    title: 'Demander une intervention',
    description: 'Solliciter une action concrète du destinataire',
    icon: Target,
    color: 'red',
    defaultMessage: 'J\'ai besoin de ton intervention pour cette action...',
    suggestedActions: ['meeting', 'email_response']
  },
  information: {
    id: 'information',
    title: 'Informer de l\'avancement',
    description: 'Tenir informé des progrès et développements',
    icon: FileText,
    color: 'green',
    defaultMessage: 'Je souhaitais te tenir informé de l\'avancement de cette action...',
    suggestedActions: ['email_response']
  }
};

const RESPONSE_ACTIONS = {
  meeting: {
    id: 'meeting',
    title: 'Proposer un rendez-vous',
    description: 'Organiser une rencontre pour discuter',
    icon: Calendar
  },
  email_response: {
    id: 'email_response',
    title: 'Demander un retour par email',
    description: 'Répondre par courrier électronique',
    icon: Mail
  },
  platform_response: {
    id: 'platform_response',
    title: 'Réponse sur la plateforme',
    description: 'Échanger directement sur Aladinn',
    icon: MessageCircle
  }
};

// =============================================
// COMPOSANTS UTILITAIRES
// =============================================

const ShareTypeSelector = ({ selectedType, onTypeChange }) => (
  <div className="space-y-3">
    <label className="block text-sm font-medium text-gray-700">
      Type de partage
    </label>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {Object.values(SHARE_TYPES).map(type => {
        const IconComponent = type.icon;
        const isSelected = selectedType === type.id;
        
        return (
          <button
            key={type.id}
            onClick={() => onTypeChange(type.id)}
            className={`p-4 border-2 rounded-lg text-left transition-all ${
              isSelected 
                ? `border-${type.color}-500 bg-${type.color}-50` 
                : 'border-gray-200 hover:border-gray-300 bg-white'
            }`}
          >
            <div className="flex items-start gap-3">
              <IconComponent 
                className={`h-5 w-5 ${
                  isSelected ? `text-${type.color}-600` : 'text-gray-500'
                } flex-shrink-0`} 
              />
              <div className="flex-1 min-w-0">
                <h4 className={`font-medium ${
                  isSelected ? `text-${type.color}-800` : 'text-gray-900'
                }`}>
                  {type.title}
                </h4>
                <p className={`text-sm mt-1 ${
                  isSelected ? `text-${type.color}-600` : 'text-gray-500'
                }`}>
                  {type.description}
                </p>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  </div>
);

const ResponseActionSelector = ({ shareType, selectedActions, onActionsChange }) => {
  const shareConfig = SHARE_TYPES[shareType];
  if (!shareConfig) return null;

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">
        Comment souhaitez-vous recevoir une réponse ?
      </label>
      <div className="space-y-2">
        {shareConfig.suggestedActions.map(actionId => {
          const action = RESPONSE_ACTIONS[actionId];
          if (!action) return null;
          
          const IconComponent = action.icon;
          const isSelected = selectedActions.includes(actionId);
          
          return (
            <label
              key={actionId}
              className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => {
                  if (e.target.checked) {
                    onActionsChange([...selectedActions, actionId]);
                  } else {
                    onActionsChange(selectedActions.filter(id => id !== actionId));
                  }
                }}
                className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <IconComponent className="h-5 w-5 text-gray-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900">{action.title}</div>
                <div className="text-sm text-gray-500 mt-1">{action.description}</div>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
};

const EmailInput = ({ selectedEmails, onEmailChange }) => {
  const [customEmail, setCustomEmail] = useState('');

  const handleAddCustom = () => {
    if (customEmail && customEmail.includes('@') && !selectedEmails.includes(customEmail)) {
      onEmailChange([...selectedEmails, customEmail]);
      setCustomEmail('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddCustom();
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Destinataires ({selectedEmails.length})
        </label>
        {selectedEmails.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedEmails.map((email, index) => (
              <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                <span className="truncate max-w-[200px]">{email}</span>
                <button
                  onClick={() => onEmailChange(selectedEmails.filter(e => e !== email))}
                  className="text-blue-600 hover:text-blue-800 flex-shrink-0"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <input
          type="email"
          placeholder="Ajouter une adresse email"
          value={customEmail}
          onChange={(e) => setCustomEmail(e.target.value)}
          onKeyPress={handleKeyPress}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <button
          onClick={handleAddCustom}
          disabled={!customEmail || !customEmail.includes('@') || selectedEmails.includes(customEmail)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">Ajouter</span>
        </button>
      </div>
    </div>
  );
};

// =============================================
// GESTIONNAIRE DE CONTENU
// =============================================

const useActionShareContent = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generateMessage = useCallback(async (shareData) => {
    setLoading(true);
    setError(null);

    try {
      const shareConfig = SHARE_TYPES[shareData.shareType];
      if (!shareConfig) throw new Error('Type de partage invalide');

      const prompt = `
        Tu es un assistant de collaboration pour la plateforme Aladinn.
        Génère un message personnalisé pour partager une action.

        CONTEXTE :
        - Objectif principal : "${shareData.userObjective}"
        - Étape courante : "${shareData.currentStep?.titre || 'Non définie'}"
        - Action à partager : "${shareData.action.title}"
        - Description : "${shareData.action.description || 'Pas de description'}"
        
        TYPE DE PARTAGE : ${shareConfig.title}
        INTENTION : ${shareConfig.description}
        
        ÉLÉMENTS INCLUS :
        ${shareData.includeObjective ? '- Contexte de l\'objectif principal' : ''}
        ${shareData.includeStep ? '- Détails de l\'étape courante' : ''}
        ${shareData.includeNotes ? '- Notes utilisateur (décisions, questions, recommandations)' : ''}
        
        ACTIONS DEMANDÉES :
        ${shareData.responseActions.map(actionId => {
          const action = RESPONSE_ACTIONS[actionId];
          return action ? `- ${action.title}` : '';
        }).filter(Boolean).join('\n')}

        Le message doit :
        1. Être professionnel et collaboratif
        2. Expliquer clairement le contexte et l'intention
        3. Mentionner les éléments partagés naturellement
        4. Être spécifique sur ce qui est attendu
        5. Inclure une invitation à découvrir Aladinn si pertinent
        6. Faire 150-200 mots
        7. Avoir un ton ${shareData.shareType === 'information' ? 'informatif' : 'engageant'}

        Message personnel existant : "${shareData.customMessage || 'Aucun'}"
        
        Améliore ce message ou génère-en un nouveau si vide.
      `;

      const response = await askGPT(prompt);
      if (!response) throw new Error('Erreur lors de la génération du message');
      return response;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { generateMessage, loading, error };
};

// =============================================
// FONCTION DE GÉNÉRATION HTML
// =============================================

const generateActionShareHTML = (shareData, finalMessage) => {
  const shareConfig = SHARE_TYPES[shareData.shareType];
  const action = shareData.action;
  
  let htmlContent = `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${shareData.userObjective || "Mon projet"}</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
            font-family: 'Segoe UI', system-ui, -apple-system, sans-serif; 
            line-height: 1.7; 
            color: #2D3748; 
            background-color: #F7FAFC;
            padding: 20px;
        }
        .container { 
            max-width: 700px; 
            margin: 0 auto; 
            background: white; 
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 4px 20px rgba(0,0,0,0.08);
        }
        
        .header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            padding: 30px;
            text-align: center;
            color: white;
        }
        
        .header h1 {
            font-size: 24px;
            font-weight: 700;
            margin-bottom: 8px;
        }
        
        .header .subtitle {
            opacity: 0.9;
            font-size: 16px;
        }
        
        .main-content {
            padding: 40px;
        }
        
        .share-type {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 8px 16px;
            border-radius: 25px;
            font-size: 13px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 20px;
            background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
            color: white;
        }
        
        .action-card {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 12px;
            padding: 24px;
            margin: 20px 0;
        }
        
        .action-title {
            font-size: 20px;
            font-weight: 600;
            color: #1a202c;
            margin-bottom: 12px;
        }
        
        .action-meta {
            display: flex;
            gap: 16px;
            margin-bottom: 16px;
            flex-wrap: wrap;
        }
        
        .meta-item {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 14px;
            color: #4a5568;
        }
        
        .action-description {
            color: #4a5568;
            line-height: 1.6;
            margin-bottom: 16px;
        }
        
        .message-section {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 24px;
            border-radius: 12px;
            margin: 20px 0;
        }
        
        .message-content {
            font-size: 16px;
            line-height: 1.7;
        }
        
        .context-section {
            margin: 20px 0;
        }
        
        .context-title {
            font-size: 18px;
            font-weight: 600;
            color: #2d3748;
            margin-bottom: 12px;
            display: flex;
            align-items: center;
            gap: 8px;
        }
        
        .context-content {
            background: #f7fafc;
            padding: 16px;
            border-radius: 8px;
            border-left: 4px solid #4299e1;
            font-size: 15px;
            line-height: 1.6;
        }
        
        .notes-section {
            background: #fef5e7;
            border: 1px solid #f6e05e;
            border-radius: 8px;
            padding: 16px;
            margin: 12px 0;
        }
        
        .notes-title {
            font-weight: 600;
            color: #744210;
            margin-bottom: 8px;
        }
        
        .notes-content {
            color: #744210;
            font-size: 14px;
            white-space: pre-line;
        }
        
        .response-actions {
            background: #e6fffa;
            border: 1px solid #81e6d9;
            border-radius: 8px;
            padding: 20px;
            margin: 20px 0;
        }
        
        .response-title {
            font-weight: 600;
            color: #234e52;
            margin-bottom: 12px;
            font-size: 16px;
        }
        
        .action-list {
            list-style: none;
            space-y: 8px;
        }
        
        .action-item {
            display: flex;
            align-items: center;
            gap: 8px;
            color: #234e52;
            font-size: 14px;
            margin-bottom: 8px;
        }
        
        .footer {
            background: #f8fafc;
            padding: 30px;
            text-align: center;
            border-top: 1px solid #e2e8f0;
        }
        
        .cta-button {
            display: inline-block;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 12px 24px;
            border-radius: 8px;
            text-decoration: none;
            font-weight: 600;
            margin: 8px;
        }
        
        .footer-text {
            font-size: 14px;
            color: #4a5568;
            margin-bottom: 15px;
        }
        
        @media (max-width: 600px) {
            .container { margin: 0 10px; }
            .main-content { padding: 25px 20px; }
            .action-meta { flex-direction: column; gap: 8px; }
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>${shareData.userObjective || "Mon projet"}</h1>
            <div class="subtitle">${shareConfig.description}</div>
        </div>
        
        <div class="main-content">
            <div class="share-type">
                📋 ${shareConfig.title}
            </div>
            
            <div class="message-section">
                <div class="message-content">
                    ${finalMessage.replace(/\n/g, '<br>')}
                </div>
            </div>`;

  // Contexte de l'objectif si inclus
  if (shareData.includeObjective && shareData.userObjective) {
    htmlContent += `
            <div class="context-section">
                <div class="context-title">
                    <span>🎯</span>
                    Mon Projet
                </div>
                <div class="context-content">
                    ${shareData.userObjective}
                </div>
            </div>`;
  }

  // Détails de l'étape si inclus
  if (shareData.includeStep && shareData.currentStep) {
    htmlContent += `
            <div class="context-section">
                <div class="context-title">
                    <span>📋</span>
                    Étape courante
                </div>
                <div class="context-content">
                    <strong>${shareData.currentStep.titre}</strong><br>
                    ${shareData.currentStep.description || 'Pas de description disponible'}
                </div>
            </div>`;
  }

  // Notes utilisateur si incluses
  if (shareData.includeNotes && shareData.stepNotes) {
    const noteSections = [
      { key: 'decisions', title: 'Décisions & Actions', icon: '✅' },
      { key: 'questions', title: 'Questions & Défis', icon: '❓' },
      { key: 'ai_recommendations', title: 'Recommandations', icon: '🧠' }
    ];

    noteSections.forEach(section => {
      const content = shareData.stepNotes[section.key];
      if (content && content.trim()) {
        htmlContent += `
            <div class="notes-section">
                <div class="notes-title">
                    ${section.icon} ${section.title}
                </div>
                <div class="notes-content">${content}</div>
            </div>`;
      }
    });
  }

  // Actions de réponse demandées
  if (shareData.responseActions.length > 0) {
    htmlContent += `
            <div class="response-actions">
                <div class="response-title">
                    Comment répondre ?
                </div>
                <ul class="action-list">`;
    
    shareData.responseActions.forEach(actionId => {
      const action = RESPONSE_ACTIONS[actionId];
      if (action) {
        htmlContent += `
                    <li class="action-item">
                        <span>•</span>
                        <span>${action.description}</span>
                    </li>`;
      }
    });
    
    htmlContent += `
                </ul>
            </div>`;
  }

  htmlContent += `
        </div>
        
        <div class="footer">
            <div class="footer-text">
                Partagé avec Aladinn - Votre assistant personnel pour réussir vos projets
            </div>
            <a href="https://aladinn-edu.web.app" class="cta-button">
                Découvrir Aladinn
            </a>
        </div>
    </div>
</body>
</html>`;

  return htmlContent;
};

// =============================================
// COMPOSANT PRINCIPAL MODAL
// =============================================

const ShareActionModal = ({ 
  show, 
  onClose, 
  action,
  userObjective,
  currentStep,
  stepNotes,
  actionPlan
}) => {
  // États principaux
  const [shareType, setShareType] = useState('advice');
  const [responseActions, setResponseActions] = useState(['email_response']);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [customMessage, setCustomMessage] = useState('');
  const [includeObjective, setIncludeObjective] = useState(false);
  const [includeStep, setIncludeStep] = useState(true);
  const [includeNotes, setIncludeNotes] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Hook pour la génération de contenu
  const { generateMessage, loading: messageLoading } = useActionShareContent();

  // Initialisation du message par défaut
  useEffect(() => {
    if (show && shareType) {
      const shareConfig = SHARE_TYPES[shareType];
      if (shareConfig && !customMessage) {
        setCustomMessage(shareConfig.defaultMessage);
      }
    }
  }, [show, shareType, customMessage]);

  // Génération automatique du message
  const handleGenerateMessage = useCallback(async () => {
    if (!action) return;

    const shareData = {
      shareType,
      action,
      userObjective,
      currentStep,
      stepNotes,
      responseActions,
      includeObjective,
      includeStep,
      includeNotes,
      customMessage
    };

    const generatedMessage = await generateMessage(shareData);
    if (generatedMessage) {
      setCustomMessage(generatedMessage);
    }
  }, [
    shareType, action, userObjective, currentStep, stepNotes, responseActions,
    includeObjective, includeStep, includeNotes, customMessage, generateMessage
  ]);

  // Préparation des données pour le partage
  const shareData = useMemo(() => ({
    shareType,
    action,
    userObjective,
    currentStep,
    stepNotes,
    responseActions,
    includeObjective,
    includeStep,
    includeNotes,
    customMessage
  }), [
    shareType, action, userObjective, currentStep, stepNotes, responseActions,
    includeObjective, includeStep, includeNotes, customMessage
  ]);

  // Envoi du partage
  const handleSendShare = async () => {
    if (selectedEmails.length === 0) {
      setError('Veuillez ajouter au moins un destinataire.');
      return;
    }

    if (!customMessage.trim()) {
      setError('Veuillez saisir un message.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const htmlContent = generateActionShareHTML(shareData, customMessage);
      const shareConfig = SHARE_TYPES[shareType];

      for (const email of selectedEmails) {
        await sendEmail(
          email,
          `${shareConfig.title} - ${action.title}`,
          htmlContent,
          true
        );
      }

      setSuccess(`Partage envoyé avec succès à ${selectedEmails.length} destinataire(s) !`);
      setTimeout(() => {
        onClose();
        setSuccess('');
        setSelectedEmails([]);
        setCustomMessage('');
      }, 2000);

    } catch (err) {
      setError('Erreur lors de l\'envoi du partage.');
      console.error('Erreur partage action:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Réinitialisation lors de l'ouverture/fermeture
  useEffect(() => {
    if (!show) {
      setError('');
      setSuccess('');
      setShowPreview(false);
    }
  }, [show]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[95vh] overflow-y-auto shadow-2xl">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Share2 className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Partager : {userObjective || "Mon projet"}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {action?.title || 'Action non définie'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Messages de feedback */}
        {success && (
          <div className="m-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            <span>{success}</span>
          </div>
        )}

        {error && (
          <div className="m-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        )}

        <div className="p-6 space-y-6">
          
          {/* Type de partage */}
          <ShareTypeSelector
            selectedType={shareType}
            onTypeChange={setShareType}
          />

          {/* Sélection des actions de réponse */}
          <ResponseActionSelector
            shareType={shareType}
            selectedActions={responseActions}
            onActionsChange={setResponseActions}
          />

          {/* Éléments contextuels à inclure */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              Éléments contextuels à partager
            </label>
            <div className="space-y-3">
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  checked={includeObjective}
                  onChange={(e) => setIncludeObjective(e.target.checked)}
                  className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <Target className="h-5 w-5 text-gray-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900">Mon Projet</div>
                  <div className="text-sm text-gray-500 mt-1">
                    {userObjective ? `"${userObjective}"` : 'Aucun objectif défini'}
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  checked={includeStep}
                  onChange={(e) => setIncludeStep(e.target.checked)}
                  className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <FileText className="h-5 w-5 text-gray-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900">Détails de l'étape</div>
                  <div className="text-sm text-gray-500 mt-1">
                    {currentStep ? `"${currentStep.titre}"` : 'Aucune étape courante'}
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="checkbox"
                  checked={includeNotes}
                  onChange={(e) => setIncludeNotes(e.target.checked)}
                  className="mt-0.5 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <Brain className="h-5 w-5 text-gray-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900">Notes utilisateur</div>
                  <div className="text-sm text-gray-500 mt-1">
                    Décisions, questions et recommandations Aladinn
                    {stepNotes && Object.keys(stepNotes).length > 0 
                      ? ` (${Object.keys(stepNotes).length} sections)`
                      : ' (aucune note)'}
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Message personnalisé */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                Message personnalisé
              </label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium px-3 py-1 rounded-lg hover:bg-blue-50 transition-colors"
                >
                  {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  <span>{showPreview ? 'Éditer' : 'Aperçu'}</span>
                </button>
                <button
                  onClick={handleGenerateMessage}
                  disabled={messageLoading}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1 px-3 py-1 rounded-lg hover:bg-blue-50 transition-colors"
                >
                  {messageLoading ? (
                    <div className="w-4 h-4 border border-blue-500 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  <span>Régénérer</span>
                </button>
              </div>
            </div>
            
            {showPreview ? (
              <div className="relative border rounded-lg overflow-hidden bg-gray-50">
                <div className="absolute top-2 right-2 z-10 bg-white/80 backdrop-blur-sm rounded px-2 py-1 text-xs text-gray-600">
                  Aperçu complet
                </div>
                <div 
                  className="p-4 overflow-y-auto text-sm bg-white"
                  style={{ height: '400px' }}
                  dangerouslySetInnerHTML={{
                    __html: generateActionShareHTML(shareData, customMessage)
                  }}
                />
              </div>
            ) : (
              <textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                className="w-full p-4 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                rows={6}
                placeholder="Votre message personnel sera intégré dans le partage..."
              />
            )}
          </div>

          {/* Destinataires */}
          <EmailInput
            selectedEmails={selectedEmails}
            onEmailChange={setSelectedEmails}
          />

          {/* Informations sur la plateforme */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <h4 className="font-semibold text-yellow-800 mb-3 flex items-center gap-2 text-sm">
              <Info className="h-4 w-4" />
              Notifications et collaboration
            </h4>
            <ul className="text-xs text-yellow-700 space-y-2">
              <li>• Le destinataire recevra une notification par email</li>
              <li>• S'il est inscrit sur Aladinn, il recevra aussi une notification sur la plateforme</li>
              <li>• Le message l'invitera à rejoindre Aladinn pour collaborer directement</li>
              <li>• Vous pourrez suivre les réponses depuis votre espace de travail</li>
            </ul>
          </div>

        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 p-6 bg-gray-50 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleSendShare}
            disabled={isLoading || !customMessage || selectedEmails.length === 0}
            className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-all font-medium ${
              isLoading || !customMessage || selectedEmails.length === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md hover:shadow-lg'
            }`}
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border border-white border-t-transparent rounded-full animate-spin" />
                <span>Envoi en cours...</span>
              </>
            ) : (
              <>
                <Send className="h-5 w-5" />
                <span>Envoyer le partage ({selectedEmails.length})</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShareActionModal;
